import { useState } from "react";
import { Printer, Tag } from "lucide-react";
import { Reveal } from "./Reveal";

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const LabelMaker = () => {
  const [soup, setSoup] = useState("");
  const [cook, setCook] = useState("");
  const [ingredients, setIngredients] = useState("");
  const [note, setNote] = useState("");

  const printLabel = () => {
    const w = window.open("", "_blank", "width=700,height=650");
    if (!w) return;
    w.document.write(`<html><head><title>Quart Label — ${esc(soup) || "Soup"}</title>
      <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,700;1,600&family=Jost:wght@400;500&family=Great+Vibes&display=swap" rel="stylesheet">
      <style>
        @page{margin:1.2cm;}
        body{font-family:'Jost',sans-serif;padding:24px;color:#17130F;}
        .label{border:2px solid #1B1611;padding:36px;max-width:420px;margin:0 auto;position:relative;}
        .hole{position:absolute;top:-12px;left:36px;width:22px;height:22px;border-radius:50%;background:#fff;border:2px solid #1B1611;}
        .soup{font-family:'Great Vibes',cursive;font-size:44px;color:#C24E21;line-height:1.1;}
        .from{font-size:10px;text-transform:uppercase;letter-spacing:3px;color:#6E655C;margin-top:14px;}
        .cook{font-family:'Fraunces',serif;font-style:italic;font-size:26px;}
        .rule{border-top:1px solid #1B1611;margin:20px 0;}
        .ing-label{font-size:10px;text-transform:uppercase;letter-spacing:3px;color:#6E655C;margin-bottom:8px;}
        .ing{font-size:13px;line-height:1.6;white-space:pre-line;}
        .note{font-family:'Great Vibes',cursive;font-size:24px;color:#7A2E2A;margin-top:16px;}
        .foot{display:flex;justify-content:space-between;margin-top:24px;padding-top:14px;border-top:1px solid #1B1611;font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#6E655C;}
        .foot b{color:#EF620D;font-weight:500;}
      </style></head>
      <body>
        <div class="label">
          <div class="hole"></div>
          <div class="soup">${esc(soup) || "Your Soup Name"}</div>
          <div class="from">from the kitchen of</div>
          <div class="cook">${esc(cook) || "Your Name"}</div>
          <div class="rule"></div>
          <div class="ing-label">Ingredients</div>
          <div class="ing">${esc(ingredients) || "Full ingredient list"}</div>
          ${note ? `<div class="note">${esc(note)}</div>` : ""}
          <div class="foot"><span>Soup Party &middot; Oct 3, 2026</span><b>freeze me!</b></div>
        </div>
        <script>window.onload=function(){setTimeout(function(){window.print();},700);};<\/script>
      </body></html>`);
    w.document.close();
  };

  return (
    <section id="labels" className="relative bg-[#A3B18A] px-6 md:px-16 lg:px-24 py-24 md:py-36" data-testid="labels-section">
      <Reveal x={-80}>
        <p className="font-body text-[11px] uppercase tracking-[0.35em] text-harvest mb-4">Before You Cook</p>
        <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-cream">
          The Quart Label Maker — <span className="font-accent text-harvest text-4xl md:text-6xl align-middle -rotate-2 inline-block">print & tape!</span>
        </h2>
        <p className="mt-5 font-body text-base md:text-lg text-fog leading-relaxed max-w-2xl">
          Every quart needs its recipe name and full ingredient list. Type it once, print one per container, and tape it on — allergy-safe and freezer-ready.
        </p>
      </Reveal>

      <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <Reveal delay={0.05}>
          <div className="notice-block p-8 md:p-10 space-y-7" data-testid="label-form">
            <div>
              <label className="field-label" htmlFor="label-soup">Soup Name</label>
              <input id="label-soup" data-testid="label-soup-input" className="field" placeholder="Roasted Tomato Basil" value={soup} onChange={(e) => setSoup(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="label-cook">From the Kitchen Of</label>
              <input id="label-cook" data-testid="label-cook-input" className="field" placeholder="Aunt June" value={cook} onChange={(e) => setCook(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="label-ingredients">Full Ingredient List</label>
              <textarea
                id="label-ingredients"
                data-testid="label-ingredients-input"
                className="field min-h-[140px] resize-y"
                placeholder={"Roma tomatoes, yellow onion, garlic, heavy cream,\nfresh basil, olive oil, sea salt, black pepper"}
                value={ingredients}
                onChange={(e) => setIngredients(e.target.value)}
              />
              <p className="mt-2 font-body text-xs text-fog/70">Every single ingredient — this is how we keep allergies safe.</p>
            </div>
            <div>
              <label className="field-label" htmlFor="label-note">Reheat Note (optional)</label>
              <input id="label-note" data-testid="label-note-input" className="field" placeholder="Simmer 15 min, stir often" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <button data-testid="label-print-btn" onClick={printLabel} className="btn-ember">
              <Printer size={16} />
              Print This Label
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="lg:pl-8">
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-fog mb-5 flex items-center gap-2">
              <Tag size={14} className="text-ember" /> Live preview
            </p>
            <div id="label-print" className="relative bg-[#FDF3D7] border-2 border-forest p-8 md:p-10 max-w-md shadow-[8px_8px_0_0_#241505] rotate-1" data-testid="label-preview">
              <div className="absolute -top-3 left-10 h-6 w-6 rounded-full bg-[#A3B18A] border-2 border-forest" />
              <p className="font-accent text-4xl md:text-5xl text-ember leading-tight">{soup || "Your Soup Name"}</p>
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-fog mt-3">from the kitchen of</p>
              <p className="font-display text-2xl md:text-3xl text-[#2A1A08] italic">{cook || "Your Name"}</p>
              <div className="my-5 border-t border-forest/40" />
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-fog mb-2">Ingredients</p>
              <p className="font-body text-sm text-[#2A1A08] leading-relaxed whitespace-pre-line">
                {ingredients || "List every ingredient here — one per line reads best on the quart."}
              </p>
              {note && <p className="font-accent text-2xl text-harvest mt-4 -rotate-1">{note}</p>}
              <div className="mt-6 pt-4 border-t border-forest/40 flex items-center justify-between">
                <p className="font-body text-[10px] uppercase tracking-[0.25em] text-fog">Soup Party · Oct 3, 2026</p>
                <p className="font-body text-[10px] uppercase tracking-[0.25em] text-ember">freeze me!</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
